import {makeAutoObservable, observable, reaction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import {compact, sumBy} from "lodash";
import {ContestStatus} from "data/enums";
import {Empty} from "data/types/global";
import {IRosterPlayer} from "data/types/roster";
import type {IPlayerCalculations, IGamePlayer} from "data/utils/player.calculations";

interface IParams {
	rosterPlayers: IRosterPlayer[];
	contestId: number;
}

export interface IRosterListController extends ViewController<IParams> {
	i18n: ILocalizationStore;

	get isLoading(): boolean;

	get players(): IGamePlayer[];

	get projPts(): Empty<number>;

	get dailyPts(): string;

	get contestStatus(): ContestStatus;
}

@injectable()
export class RosterListController implements IRosterListController {
	@observable _disposers: ReturnType<typeof reaction>[] = [];
	@observable private _contestId: number = 0;
	@observable private _rosterPlayers: IRosterPlayer[] = [];

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.PlayerCalculations)
		private readonly _playerCalculations: IPlayerCalculations,
		@inject(Bindings.ContestsStore) private readonly _contestStore: IContestsStore
	) {
		makeAutoObservable(this);
	}

	get isLoading(): boolean {
		return this._playerCalculations.isLoading(this._contestId);
	}

	get players(): IGamePlayer[] {
		if (!this._contestId || !this._rosterPlayers.length) {
			return [];
		}
		return compact(
			this._rosterPlayers.map((it) => {
				return this._playerCalculations.getGamePlayerById(it.playerId, this._contestId);
			})
		);
	}

	get contestStatus() {
		return this._contestStore.currentContest?.status ?? ContestStatus.Open;
	}

	get projPts() {
		const players = this.players;
		if (!players.length) {
			return;
		}
		return sumBy(players, (rosterPlayer) => {
			return rosterPlayer.contestStats?.projectedPoints || 0;
		});
	}

	get dailyPts() {
		const players = this.players;

		const points = sumBy(players, (rosterPlayer) => {
			return rosterPlayer?.contestStats?.fantasyPoints || 0;
		});

		return `${points || "--"}`;
	}

	init(param: IParams) {
		this._rosterPlayers = param.rosterPlayers;
		this._contestId = param.contestId;
		this._disposers = [];
	}

	onChange(param: IParams) {
		this._contestId = param.contestId;
		this._rosterPlayers = param.rosterPlayers;
	}

	dispose() {
		this._disposers.forEach((dispose) => dispose());
	}
}
